import {clearAllOnboardingWizardState} from 'common/helpers/onboardingWizardStorage'
// The blob a tenant is left with after the clear. Kept in its own redux-free module so the
// one piece of this flow with real logic in it can be tested directly — see that file for why
// it DROPS the previous run's buckets rather than merging them (bug 315).
import {buildPostSampleDeletePreferences} from 'common/helpers/postSampleDeletePreferences'
import {
  EMPTY_SAMPLE_DATA_COUNTS,
  OnboardingSampleDataPrefs,
  SampleDataLiveCounts,
} from 'common/types/onboardingSampleData'
import {Dispatch} from 'redux'
import {MainReducer} from 'setup'
import {getUserPreferences, updateUserPreferences} from 'setup/redux/Actions/Dashboard/dashboard'
import {deleteSampleData} from 'setup/redux/Actions/General/bulkDelete'

type UserPreferences = Record<string, unknown> | null | undefined

export const getOnboardingSampleDataPrefs = (
  userPreferences: UserPreferences
): OnboardingSampleDataPrefs | undefined => {
  const wizard = userPreferences?.OnboardingWizard as Record<string, unknown> | undefined

  return wizard?.sampleData as OnboardingSampleDataPrefs | undefined
}

export const hasOnboardingSampleData = (userPreferences: UserPreferences): boolean =>
  getOnboardingSampleDataPrefs(userPreferences)?.active === true

const arrayLength = (rows: unknown[] | null | undefined): number =>
  Array.isArray(rows) ? rows.length : 0

const subjectRows = (mainReducer: MainReducer): Record<string, unknown>[] => {
  if (Array.isArray(mainReducer.subjects) && mainReducer.subjects.length > 0) {
    return mainReducer.subjects as Record<string, unknown>[]
  }

  if (Array.isArray(mainReducer.get_user_subjects) && mainReducer.get_user_subjects.length > 0) {
    return mainReducer.get_user_subjects as Record<string, unknown>[]
  }

  return []
}

/** Counts from Redux — populated by the same list APIs MasterLayout loads on login. */

export const getSampleDataCountsFromRedux = (mainReducer: MainReducer): SampleDataLiveCounts => {
  const stats = mainReducer.timetable_list_stats

  return {
    teachers: arrayLength(mainReducer.teachers) || Number(stats?.teacher_count ?? 0),

    students: arrayLength(mainReducer.students) || Number(stats?.student_count ?? 0),

    subjects: subjectRows(mainReducer).length || Number(stats?.subject_count ?? 0),

    classes: arrayLength(mainReducer.classes) || Number(stats?.class_count ?? 0),

    venues: Number(stats?.classroom_count ?? 0),

    grades: arrayLength(mainReducer.grades),

    sections: arrayLength(mainReducer.sections),
  }
}

export const deleteAllSampleData = (dispatch: Dispatch): Promise<void> =>
  new Promise((resolve, reject) => {
    dispatch(
      deleteSampleData(
        () => resolve(),

        (err: unknown) => reject(err)
      ) as never
    )
  })

const ONBOARDING_LOCAL_STORAGE_KEYS = [
  'onboarding_complete',

  'onboarding_import_type',

  'onboarding_welcome_seen',

  'onboarding_sample_data_unavailable',

  'onboarding_manual_data',

  'onboarding_csv_basic_setup',

  'onboarding_csv_class_names',
] as const

const clearOnboardingLocalStorage = () => {
  ONBOARDING_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key))

  // Wizard state is keyed per tenant (onboardingWizardState:<urddId>), so it can't be listed
  // above — this clears every tenant's, plus the legacy un-keyed key.
  clearAllOnboardingWizardState()
}

const putUserPreferences = (
  dispatch: Dispatch,

  preferences: Record<string, unknown>
): Promise<void> =>
  new Promise((resolve, reject) => {
    dispatch(
      updateUserPreferences(
        {preferences},

        () => resolve(),

        (err: unknown) => reject(err)
      ) as never
    )
  })

/** GET preferences → merge cleared onboarding flags → PUT preferences. */

export const resetOnboardingAfterSampleDataDelete = (dispatch: Dispatch): Promise<void> =>
  new Promise((resolve, reject) => {
    dispatch(
      getUserPreferences(
        async (res: {result?: Record<string, unknown>}) => {
          try {
            const merged = buildPostSampleDeletePreferences(res?.result ?? {})

            await putUserPreferences(dispatch, merged)

            clearOnboardingLocalStorage()

            resolve()
          } catch (err) {
            reject(err)
          }
        },

        async () => {
          try {
            const merged = buildPostSampleDeletePreferences({})

            await putUserPreferences(dispatch, merged)

            clearOnboardingLocalStorage()

            resolve()
          } catch (err) {
            reject(err)
          }
        }
      ) as never
    )
  })

export const ONBOARDING_PATH_AFTER_SAMPLE_DELETE = '/onboarding'

export const ONBOARDING_SAMPLE_DATA_CLEARED_KEY = 'onboarding_sample_data_cleared'

export const markOnboardingSampleDataCleared = (): void => {
  localStorage.setItem(ONBOARDING_SAMPLE_DATA_CLEARED_KEY, 'true')
}

export const clearOnboardingSampleDataClearedFlag = (): void => {
  localStorage.removeItem(ONBOARDING_SAMPLE_DATA_CLEARED_KEY)
}

/** One-time dashboard popup: set when the sample-credits grant succeeds, consumed on the dashboard. */
export const SAMPLE_CREDITS_POPUP_PENDING_KEY = 'onboarding_sample_credits_popup_pending'

export const markSampleCreditsPopupPending = (): void => {
  localStorage.setItem(SAMPLE_CREDITS_POPUP_PENDING_KEY, 'true')
}

/** Returns true exactly once after the flag was set, clearing it so the popup never repeats. */
export const consumeSampleCreditsPopupPending = (): boolean => {
  if (localStorage.getItem(SAMPLE_CREDITS_POPUP_PENDING_KEY) !== 'true') return false
  localStorage.removeItem(SAMPLE_CREDITS_POPUP_PENDING_KEY)
  return true
}

export {EMPTY_SAMPLE_DATA_COUNTS}
